<template>
  <div class="location-details-container">
    <div v-if="loading" class="loading-container">
      <AppPageLoader>{{ $t('general.loading') }}</AppPageLoader>
    </div>

    <div v-else-if="error" class="error-container">
      <font-awesome-icon icon="fa-exclamation-triangle" class="error-icon" />
      <h2>{{ $t('forms.errorLoading') }}</h2>
      <p>{{ error }}</p>
      <BaseButton to="/establishments" icon="fa-arrow-left">
        {{ $t('establishments.backToEstablishments') }}
      </BaseButton>
    </div>

    <div v-else-if="!location || !location.uuid" class="error-container">
      <font-awesome-icon icon="fa-exclamation-triangle" class="error-icon" />
      <h2>{{ $t('forms.errorLoading') }}</h2>
      <p>{{ $t('locations.locationNotAvailable') }}</p>
      <BaseButton to="/establishments" icon="fa-arrow-left">
        {{ $t('establishments.backToEstablishments') }}
      </BaseButton>
    </div>

    <div v-else>
      <div class="header detail-page-header">
        <router-link :to="`/establishments/${location.establishment_uuid}`" class="back-button">
          <font-awesome-icon icon="fa-arrow-left" />
          {{ $t('locations.backToEstablishment') }}
        </router-link>
        <div class="detail-page-header__actions">
          <BaseButton variant="secondary" :to="`/locations/${location.slug || location.uuid}/tables`" icon="fa-table">
            {{ $t('tables.manageTables') }}
          </BaseButton>
          <BaseButton
            v-if="canMutateInProject"
            variant="info"
            :to="`/locations/${location.slug || location.uuid}/edit`"
            icon="fa-pen"
          >
            {{ $t('establishments.edit') }}
          </BaseButton>
          <BaseButton
            v-if="canMutateInProject"
            :variant="location.is_active ? 'danger' : 'success'"
            :icon="location.is_active ? 'fa-toggle-on' : 'fa-toggle-off'"
            @click="toggleStatus"
          >
            {{ location.is_active ? $t('establishments.deactivate') : $t('establishments.activate') }}
          </BaseButton>
        </div>
        <div class="header-left">
          <div class="location-title">
            <div class="title-with-badge">
              <h1>{{ location.name }}</h1>
              <span class="status-badge" :class="{ active: location.is_active }">
                {{ location.is_active ? $t('establishments.active') : $t('establishments.inactive') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="content-grid">
        <div class="main-content">

          <div class="card">
            <AnalyticsPanel type="location" :entity-id="location.uuid" />
          </div>

          <div class="details-row">
            <div class="card">
              <div class="card-header">
                <h2>{{ $t('locations.contactDetails') }}</h2>
              </div>
              <div class="card-body">
                <div class="info-list">
                  <div class="info-item" v-if="location.details?.address">
                    <font-awesome-icon icon="fa-map-marker-alt" class="info-icon" />
                    <span class="info-text">{{ location.details.address }}</span>
                  </div>
                  <div class="info-item" v-if="location.details?.email">
                    <font-awesome-icon icon="fa-envelope" class="info-icon" />
                    <a :href="`mailto:${location.details.email}`" class="info-text link">
                      {{ location.details.email }}
                    </a>
                  </div>
                  <div class="info-item" v-if="location.details?.phone">
                    <font-awesome-icon icon="fa-phone" class="info-icon" />
                    <a :href="`tel:${location.details.phone}`" class="info-text link">
                      {{ location.details.phone }}
                    </a>
                  </div>

                  <div class="meta-row">
                    <div class="info-item" v-if="location.created_at">
                      <font-awesome-icon icon="fa-clock" class="info-icon" />
                      <span class="info-text">{{ $t('establishments.created') }}: {{ formatDate(location.created_at)
                      }}</span>
                    </div>
                    <div class="info-item" v-if="location.updated_at">
                      <font-awesome-icon icon="fa-clock" class="info-icon" />
                      <span class="info-text">{{ $t('establishments.lastUpdated') }}: {{ formatDate(location.updated_at)
                      }}</span>
                    </div>
                    <div class="info-item error-message" v-if="!location.details">
                      <font-awesome-icon icon="fa-exclamation-triangle" class="info-icon error-icon" />
                      <span class="info-text">{{ $t('locations.detailsNotAvailable') }}</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div class="card" v-if="location.details?.latitude && location.details?.longitude">
              <div class="card-header">
                <h2>{{ $t('locations.mapLocation') }}</h2>
              </div>
              <div class="card-body p-0 map-body">
                <div id="map" ref="mapRef" class="map-container"></div>
                <div class="coordinates-display">
                  <div class="coordinate">
                    <span class="coordinate-label">{{ $t('locations.latitude') }}:</span>
                    <span class="coordinate-value">{{ location.details.latitude }}</span>
                  </div>
                  <div class="coordinate">
                    <span class="coordinate-label">{{ $t('locations.longitude') }}:</span>
                    <span class="coordinate-value">{{ location.details.longitude }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="card" v-else-if="location.details">
              <div class="card-header">
                <h2>{{ $t('locations.mapLocation') }}</h2>
              </div>
              <div class="card-body">
                <div class="empty-state">
                  <font-awesome-icon icon="fa-map-marker-alt" class="empty-icon" />
                  <p>{{ $t('locations.coordinatesNotAvailable') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-header">
              <h2>{{ $t('locations.workingHours') }}</h2>
            </div>
            <div class="card-body">
              <div v-if="!location.details?.working_hours || location.details.working_hours.length === 0"
                class="empty-state">
                <p>{{ $t('locations.noWorkingHours') }}</p>
              </div>
              <div v-else class="working-hours-list">
                <div v-for="(hours, index) in location.details.working_hours" :key="index" class="working-hours-item">
                  <div class="hours-days">
                    <font-awesome-icon icon="fa-calendar-day" class="hours-icon" />
                    <span v-if="hours.day_from === hours.day_to">
                      {{ $t(`locations.days.${DayMap[hours.day_from]}`) }}
                    </span>
                    <span v-else>
                      {{ $t(`locations.days.${DayMap[hours.day_from]}`) }} - {{
                        $t(`locations.days.${DayMap[hours.day_to]}`) }}
                    </span>
                  </div>
                  <div class="hours-time">
                    <font-awesome-icon icon="fa-clock" class="hours-icon" />
                    {{ formatTime(hours.time_from) }} - {{ formatTime(hours.time_to) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <POSIntegrationCard
            :key="`pos-${location.uuid}`"
            :location-id="location.uuid"
            :read-only="!canMutateInProject"
          />
          <POSMenuMappingsCard
            v-if="location.uuid && location.establishment_uuid"
            :key="`pos-map-${location.uuid}`"
            :location-id="location.uuid"
            :establishment-id="location.establishment_uuid"
            :read-only="!canMutateInProject"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { locationApi } from '@/services/api';
import { Location, DayMap } from '@/types/establishments';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from '@/components/analytics/AnalyticsPanel.vue';
import POSIntegrationCard from '@/components/pos/POSIntegrationCard.vue';
import POSMenuMappingsCard from '@/components/pos/POSMenuMappingsCard.vue';
import { useProjectPermissions } from '@/composables/useProjectPermissions';
import BaseButton from '@/components/shared/BaseButton.vue';
import { getApiErrorMessage } from '@/utils/apiErrors';

const route = useRoute();
const { canMutateInProject } = useProjectPermissions();
const router = useRouter();
const { t, locale } = useI18n();
const id = computed(() => route.params.id as string);
const mapRef = ref<HTMLElement | null>(null);

const location = ref<Location>({} as Location);
const loading = ref(true);
const error = ref<string | null>(null);

const fetchLocation = async () => {
  loading.value = true;
  error.value = null;

  if (!id.value) {
    error.value = t('locations.locationIdNotFoundInUrl');
    loading.value = false;
    return;
  }

  try {
    const fetchedLocation = await locationApi.getById(id.value);

    if (!fetchedLocation || !fetchedLocation.uuid) {
      throw new Error(t('locations.emptyApiResponse'));
    }

    location.value = fetchedLocation;
  } catch (err: any) {
    if (err.response) {
      if (err.response.status === 401) {
        error.value = t('locations.notLoggedInOrSessionExpired');
        router.push('/login');
        return;
      } else if (err.response.status === 404) {
        error.value = t('locations.locationNotFound');
      } else {
        error.value = `${t('locations.errorLoading')} (${err.response.status}): ${getApiErrorMessage(err, t('locations.unknownError'))}`;
      }
    } else if (err.request) {
      error.value = t('locations.noServerResponse');
    } else {
      error.value = `${t('locations.errorLoading')}: ${err.message || t('locations.unknownError')}`;
    }
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const formatTime = (timeString: string) => {
  if (timeString.includes(':')) {
    const parts = timeString.split(':');
    return `${parts[0]}:${parts[1]}`;
  }
  return timeString;
};

const initMap = () => {
  if (!mapRef.value) return;
  if (!location.value.details?.latitude || !location.value.details?.longitude) return;

  if (typeof google === 'undefined' || !google.maps) {
    window.initLocationDetailsMapCallback = renderMap;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places&language=${locale.value}&loading=async&callback=initLocationDetailsMapCallback`;
    script.async = true;
    script.defer = true;

    document.head.appendChild(script);
  } else {
    renderMap();
  }
};

const renderMap = () => {
  if (!mapRef.value) return;
  if (!location.value.details?.latitude || !location.value.details?.longitude) return;

  const position = {
    lat: location.value.details.latitude,
    lng: location.value.details.longitude
  };

  const mapOptions: google.maps.MapOptions = {
    center: position,
    zoom: 15,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    zoomControl: true
  };

  const map = new google.maps.Map(mapRef.value, mapOptions);

  new google.maps.Marker({
    position,
    map,
    title: location.value.name
  });
};

const toggleStatus = async () => {
  try {
    await locationApi.setStatus(id.value, !location.value.is_active);
    location.value.is_active = !location.value.is_active;
  } catch (error) {
    alert('Došlo je do greške pri promjeni statusa lokacije.');
  }
};

watch(id, () => {
  void fetchLocation().then(() => {
    initMap();
  });
}, { immediate: true });

declare global {
  interface Window {
    initLocationDetailsMapCallback?: () => void;
  }
}
</script>

<style scoped>
.location-details-container {
  margin: 0 auto;
}

.header:not(.detail-page-header) {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-left {
  flex: 1;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #4b5563;
  font-size: 0.95rem;
  text-decoration: none;
  margin-bottom: 0.5rem;
}

.back-button:hover {
  color: #D8670C;
}

.title-with-badge {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.location-title h1 {
  font-weight: 600;
  margin: 0;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #fef2f2;
  color: #b91c1c;
}

.status-badge.active {
  background-color: #dcfce7;
  color: #15803d;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

.details-row {
  display: grid;
  grid-template-columns: minmax(280px, 0.85fr) minmax(0, 1.15fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  align-items: stretch;
}

.details-row .card {
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.details-row .card-body {
  flex: 1;
}

.meta-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.card-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.card-body {
  padding: 1.5rem;
}

.card-body.p-0 {
  padding: 0;
}

.info-list {
  display: grid;
  gap: 1rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #6b7280;
}

.info-icon {
  width: 1.5rem;
  color: #9ca3af;
}

.info-text {
  font-size: 0.95rem;
}

.info-text.link {
  color: #2563eb;
  text-decoration: none;
}

.info-text.link:hover {
  text-decoration: underline;
}

.map-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.map-container {
  height: 280px;
  width: 100%;
}

.coordinates-display {
  display: flex;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-top: 1px solid #f3f4f6;
  gap: 2rem;
}

.coordinate {
  display: flex;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.coordinate-label {
  color: #6b7280;
  font-weight: 500;
}

.coordinate-value {
  color: #111827;
}

.working-hours-list {
  display: grid;
  gap: 1rem;
}

.working-hours-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 0.5rem;
}

.hours-days,
.hours-time {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #4b5563;
  font-size: 0.95rem;
}

.hours-icon {
  color: #9ca3af;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #6b7280;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 1.5rem;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
}

.error-icon {
  font-size: 3rem;
  color: #ef4444;
  margin-bottom: 1rem;
}

.error-container h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #111827;
}

.error-container p {
  margin: 0 0 2rem 0;
  color: #4b5563;
}

@media (max-width: 768px) {

  .header:not(.detail-page-header) {
    flex-direction: column;
    gap: 1rem;
  }

  .header-left {
    width: 100%;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
    gap: 0.5rem;
  }

  .location-title h1 {
    font-size: 1.5rem;
  }

  .title-with-badge {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .content-grid {
    grid-template-columns: 1fr;
  }

  .details-row {
    grid-template-columns: 1fr;
  }

  .card {
    padding: 1rem;
  }

  .info-list {
    flex-direction: column;
    gap: 0.75rem;
  }

  .info-item {
    width: 100%;
  }

  .working-hours-item {
    flex-direction: column;
    gap: 0.5rem;
  }

  .coordinates-display {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
  }

  .map-container {
    height: 250px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }

  .card-body {
    padding: 1rem 0.75rem;
  }

  .meta-row {
    gap: 0.75rem;
  }

  .info-list>div>.info-item {
    margin-bottom: 0.5rem;
  }

  .working-hours-item {
    padding: 0.75rem;
  }
}

</style>